/**
 * Regelversion 8: Probleme, die der echten Lage folgen statt einem festen Drehbuch.
 *
 * Früher hatte jedes Problem einen festen Startmonat und stieg jeden Monat um eine feste Rate.
 * Für diese vier Probleme gibt es jetzt einen Zielwert aus der Lage der Partie, dem der Druck folgt:
 *  - Milliardenlöcher im Haushalt: Abstand des Haushalts zur Schuldenbremse. Taucht nur auf, wenn es eng wird.
 *  - Hohe Abgabenlast: Steuern und Belastung von Arbeitnehmern und Unternehmen.
 *  - Industrie wandert ab: Standortflucht, Energiepreise, Belastung der Unternehmen, Wachstum.
 *  - Kommunen pleite: Hilfe für Länder und Kommunen, Druck durch Unterbringung Geflüchteter.
 * Gesetze wirken weiter: Ihr dauerhafter Effekt (cut) senkt den Zielwert.
 */
import { S } from './state';
import { clamp, de } from './util';
import { since } from './version';
import { isv } from './opinion';
import { debtLimit } from './budget';
import { loadOf } from './burden8';
import { relevant9, demoDesc } from './demo9';

/** Spielraum bis zur Schuldenbremse in Mrd € (negativ = Grenze gerissen). */
export const budgetRoom = (): number => S.saldo - debtLimit();

const TARGET: Record<string, () => number> = {
  haushaltsloch: () => 45 - budgetRoom() * 3,
  steuer: () => 38 + (S.ein || 0) * 1.2 + loadOf('arb') * 1.2 + loadOf('unt') * .5,
  industrie: () => 38 + (S.flightU || 0) * 6 + Math.max(0, isv('energie') - 50) * .4 + loadOf('unt') * .5 - (S.gr - 1) * 6,
  kommunen: () => 46 + (isv('asyl') - 52) * .3 - ((S.adm as any)?.land || 0) * 7,
};
/** Darf das Problem überhaupt (wieder) auftauchen? */
const RELEVANT: Record<string, () => boolean> = {
  haushaltsloch: () => budgetRoom() < 6,
  steuer: () => TARGET.steuer() >= 35,
  industrie: () => TARGET.industrie() >= 35,
  kommunen: () => TARGET.kommunen() >= 35,
};

export const isDyn = (id: string): boolean => since(8) && !!TARGET[id];
export const dynTarget = (id: string): number => clamp(TARGET[id](), 0, 100);
export const dynRelevant = (id: string): boolean => (!isDyn(id) || RELEVANT[id]()) && relevant9(id);

/** Monatliche Bewegung des Problemdrucks. `noise` ist der Zufallsanteil wie bei allen Problemen. */
export function dynStep(id: string, st: any, noise: number, etat: number): number {
  const target = dynTarget(id) * (1 - (st.cut || 0) * .6);
  return clamp(st.sev + (target - st.sev) * .1 + noise - etat, 0, 100);
}

/** Beschreibung passend zur Lage (nur für die vier Probleme, sonst der feste Text). */
export function issueDesc(I: any): string {
  const d9 = since(9) ? demoDesc(I.id) : null; if (d9) return d9;
  if (!isDyn(I.id)) return I.d;
  if (I.id === 'haushaltsloch') {
    const r = budgetRoom();
    return r < 0
      ? `Der Haushalt reißt die Schuldenbremse um ${de(-r, 1)} Milliarden Euro im Jahr. Zinsen und neue Ausgaben fressen den Spielraum, Karlsruhe und Brüssel schauen genau hin.`
      : `Bis zur Schuldenbremse bleiben nur noch ${de(r, 1)} Milliarden Euro im Jahr. Jede neue Ausgabe und jeder Konjunktureinbruch kann das Loch aufreißen.`;
  }
  if (I.id === 'steuer') { const e = (S.ein || 0) + loadOf('arb'); return e > 1 ? `Beschäftigte und Mittelstand klagen über eine der höchsten Abgabenlasten der Industrieländer, und eure Regierung hat sie um ${de(e, 0)} Milliarden Euro im Jahr erhöht.` : I.d; }
  if (I.id === 'industrie' && (S.flightU || 0) > .5) return `Chemie, Stahl und Maschinenbau verlagern Produktion ins Ausland. Die Standortflucht kostet inzwischen ${de(S.flightU || 0, 1)} Milliarden Euro Steuern im Jahr.`;
  return I.d;
}
